import classnames from 'classnames';
import gsap from 'gsap';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { includes, keyBy } from 'lib/helpers';

import { CRUISE_DETAIL_MODAL, DEFAULT, FILTERS_MODAL } from 'components/Scroller/constants';
import { withEnableDisableScroller } from 'components/Scroller/context';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { DEPARTURE_PORT, DESTINATION, PORT, SHIP } from 'contexts/CruiseSearchContext/constants';
import { CruiseSelectionContext } from 'contexts/CruiseSelectionContext';

import CloseIcon from 'components/svg/CloseIcon';
import SearchIcon from 'components/svg/SearchIcon';
import _uniqBy from 'lodash/uniqBy';
import { CloseButton, ExplanatoryText, Input, InputWrapper, SearchIconWrapper, Suggestion, SuggestionsWrapper, Underlay, Wrapper } from './styles';

import { SEARCH_BAR_WRAPPER_CLASS, SEARCH_CLOSE_BUTTON_CLASS, SEARCH_SUGGESTION_CLASS, SUGGESTIONS_WRAPPER_CLASS } from './constants';

const flattenGroupOptions = (groups) => groups.flatMap((g) => g.options);

const makeDestinationOptions = ({ searchDictionary, sectionLabels }) => {
	const options = searchDictionary?.[DESTINATION]?.options;
	const labelsAndCodes = options
		.map((o) => {
			if (!o?.label || !o?.code) {
				return;
			}

			return {
				label: o.label,
				code: o.code,
				filterName: DESTINATION,
				append: sectionLabels?.labelShipAppender,
			};
		})
		.filter(Boolean);

	return labelsAndCodes;
};

const makePortOptions = ({ searchDictionary, facetOptions }) => {
	const options = searchDictionary?.[PORT]?.options;
	const labelsAndCodes = options
		.map((o) => {
			const disabled = !facetOptions?.[PORT]?.[o?.code]?.count > 0;

			if (!o?.label || !o?.code) {
				return;
			}

			return {
				label: o.label,
				code: o.code,
				filterName: PORT,
				append: '',
				disabled,
			};
		})
		.filter(Boolean);
	return labelsAndCodes;
};

const makeShipOptions = ({ searchDictionary, sectionLabels }) => {
	const options = searchDictionary?.[SHIP]?.options;
	const labelsAndCodes = options
		.map((o) => {
			if (!o?.label || !o?.code) {
				return;
			}

			return {
				label: o.label,
				code: o.code,
				filterName: SHIP,
				prepend: sectionLabels?.labelShipAppender,
			};
		})
		.filter(Boolean);

	return labelsAndCodes;
};

const makeDeparturePortOptions = ({ searchDictionary, sectionLabels }) => {
	const groups = searchDictionary?.[DEPARTURE_PORT]?.groups;
	const options = flattenGroupOptions(groups);
	const labelsAndCodes = options
		.map((o) => {
			if (!o?.label || !o?.code) {
				return;
			}

			return {
				label: o.label,
				code: o.code,
				filterName: DEPARTURE_PORT,
				prepend: sectionLabels?.labelCompareDepartingFrom,
			};
		})
		.filter(Boolean);

	return labelsAndCodes;
};

const searchTypes = {
	[DESTINATION]: {
		make: makeDestinationOptions,
	},
	[PORT]: {
		make: makePortOptions,
	},
	[SHIP]: {
		make: makeShipOptions,
	},
	[DEPARTURE_PORT]: {
		make: makeDeparturePortOptions,
	},
};

const ARROW_DOWN = 40;
const ARROW_UP = 38;
const ESC = 27;
const SHIFT = 9;

/**
 * Search input component
 *
 * @param {object} passedRef - Used for animations
 * @param {array} searchesAllowing - Must be an option from allowingTypes array (derived from searchTypes array); defaults to DESTINATION
 * @param {func} enableScroller -
 * @param {func} disableScroller -
 * @param {string} scrollerId -
 * @param {bool} applyFiltersOnSelect - When true, filter is immediately applied and queried (vs. simply updating searchSelecting in the CruiseSearchContext)
 * @param {string} placeholder - Input's placeholder value
 * @param {array} mockFilterOptions - Used for mocking tests and in Storybook
 */

function SearchBar({
	passedRef,
	searchesAllowing = [DESTINATION],
	enableScroller,
	disableScroller,
	applyFiltersOnSelect = false,
	placeholder,
	mockFilterOptions,
	scrollerId,
}) {
	const {
		filterOptions,
		addToSearchSelectingFilter,
		applyFilters,
		globalization,
		fetchAndUpdateFacetOptions,
		facetOptions,
		resetSearchSelectingFilter,
	} = useContext(CruiseSearchContext);
	const { isMobile } = useContext(CruiseSelectionContext);

	const { filtersDictionary, compareDictionary } = globalization?.dictionaries || {};
	const { labelShipAppender } = filtersDictionary;
	const { labelCompareDepartingFrom } = compareDictionary;

	const [search, setSearch] = useState('');
	const [suggestions, setSuggestions] = useState([]);
	const [focus, setFocus] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(-1);

	const ref = useRef();
	const wrapperRef = passedRef || ref;
	const inputRef = useRef();
	const suggestionsRef = useRef();
	const searchOptions = useRef([]);

	const open = suggestions?.length > 0;
	const showAsModal = isMobile && open;

	const resetSearch = () => setSearch('');
	const resetCurrentIndex = () => setCurrentIndex(-1);

	const onKeydown = useCallback(
		(event) => {
			if (!open) return;

			const { which } = event;
			const nextIndex = (currentIndex + 1) % suggestions.length;
			const prevIndex = (suggestions.length + (Math.max(currentIndex, 0) - 1)) % suggestions.length;
			const suggestionInFocus = process.browser && document.activeElement.classList.contains(SEARCH_SUGGESTION_CLASS);

			switch (which) {
				case ARROW_DOWN:
					event.preventDefault();
					setCurrentIndex(nextIndex);
					break;

				case ARROW_UP:
					event.preventDefault();
					setCurrentIndex(prevIndex);
					break;

				case SHIFT:
					if (!suggestionInFocus) {
						resetCurrentIndex();
					}
					break;

				case ESC:
					resetSearch();
					break;

				default:
					break;
			}
		},
		[open, currentIndex, suggestions?.length],
	);

	const onClick = useCallback((event) => {
		const targetIsChild = wrapperRef.current.contains(event?.target);
		if (targetIsChild) return;

		resetSearch();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		window.addEventListener('keydown', onKeydown);
		return () => {
			window.removeEventListener('keydown', onKeydown);
		};
	}, [onKeydown]);

	useEffect(() => {
		window.addEventListener('click', onClick);
		return () => {
			window.removeEventListener('click', onClick);
		};
	}, [onClick]);

	useEffect(() => {
		if (showAsModal) {
			const { top } = wrapperRef.current.getBoundingClientRect();
			const rest = searchesAllowing?.[0] === PORT ? 115 : 75;
			const y = top > 150 ? -(top - rest) : 0;
			gsap.killTweensOf(wrapperRef.current);
			gsap.to(wrapperRef.current, {
				y,
				duration: 0.4,
				ease: 'cubic-bezier(0,0,0,0)',
			});
			disableScroller();
		} else {
			gsap.killTweensOf(wrapperRef.current);
			gsap.to(wrapperRef.current, {
				y: 0,
				duration: 0.2,
				ease: 'cubic-bezier(0,0,0,0)',
			});
			enableScroller();
		}
	}, [showAsModal, disableScroller, enableScroller, wrapperRef]);

	/**
	 * Create list of options to search
	 * from searchesAllowing
	 */
	useEffect(() => {
		let options = mockFilterOptions || filterOptions;
		const searchDictionary = keyBy(options, 'key');

		searchOptions.current = [];
		const sectionLabels = {
			labelCompareDepartingFrom,
			labelShipAppender,
		};
		searchesAllowing?.forEach((type) => {
			const data = { searchDictionary, sectionLabels, facetOptions };
			const options = searchTypes[type]?.make(data);
			if (Array.isArray(options)) {
				searchOptions.current.push(...options);
			}
		});
		searchOptions.current = _uniqBy(searchOptions.current, 'code');
	}, [filterOptions, mockFilterOptions, searchesAllowing]);

	/**
	 * Do search
	 * Update search suggestions on search change
	 */
	useEffect(() => {
		if (search?.length < 2) {
			setSuggestions([]);
			resetCurrentIndex();
			return;
		}

		const nextSuggestions = (searchOptions.current || []).filter(({ label }) => includes((label || '').toLowerCase(), (search || '').toLowerCase()));
		setSuggestions(nextSuggestions);
	}, [search]);

	useEffect(() => {
		if (!open) return;
		const suggestionEls = suggestionsRef.current?.children || [];
		const elToFocus = suggestionEls[currentIndex];
		if (!elToFocus) return;

		elToFocus.focus();
	}, [currentIndex, open]);

	const showSearchIcon = !search?.length;
	const searchIconClasses = classnames({
		show: showSearchIcon,
		hide: !showSearchIcon,
	});
	const closeButtonClasses = classnames({
		show: !showSearchIcon,
		hide: showSearchIcon,
		[SEARCH_CLOSE_BUTTON_CLASS]: true,
	});
	const wrapperClasses = classnames({
		open,
		focus,
		[SEARCH_BAR_WRAPPER_CLASS]: true,
	});

	const onInputFocus = () => {
		setFocus(true);
		resetCurrentIndex();
	};
	const onInputBlur = () => {
		setFocus(false);
	};

	const underlayClasses = classnames({ show: showAsModal });

	return (
		<>
			<Underlay className={underlayClasses} aria-hidden={showAsModal} />
			<Wrapper ref={wrapperRef} className={wrapperClasses} scrollerId={scrollerId}>
				<InputWrapper>
					<Input
						className="search-bar-input"
						name="search"
						value={search}
						ref={inputRef}
						placeholder={placeholder}
						onFocus={onInputFocus}
						onBlur={onInputBlur}
						onChange={({ target }) => setSearch(target?.value || '')}
					/>
					<SearchIconWrapper className={searchIconClasses}>
						<SearchIcon />
					</SearchIconWrapper>
					<CloseButton
						type="button"
						onClick={resetSearch}
						className={closeButtonClasses}
						aria-hidden={showSearchIcon}
						disabled={showSearchIcon}
						onFocus={resetCurrentIndex}
					>
						<CloseIcon />
					</CloseButton>
				</InputWrapper>
				<SuggestionsWrapper ref={suggestionsRef} className={SUGGESTIONS_WRAPPER_CLASS}>
					{suggestions.map((suggestion, i) => {
						const { label, prepend, append, code, filterName, disabled } = suggestion;
						const onClick = (isDisabled) => {
							if (isDisabled) return;
							const callback = () => {
								if (applyFiltersOnSelect) {
									applyFilters();
								}
								if (searchesAllowing?.[0] === PORT) {
									fetchAndUpdateFacetOptions();
								}
							};
							if (searchesAllowing?.[0] === PORT) {
								resetSearchSelectingFilter(DESTINATION, addToSearchSelectingFilter(filterName, code, callback));
							} else {
								addToSearchSelectingFilter(filterName, code, callback);
							}
							resetSearch();
						};

						const classesSuggestion = classnames({
							SEARCH_SUGGESTION_CLASS,
							disabled,
						});

						return (
							<Suggestion
								index={i}
								type="button"
								aria-label={label}
								data-code={code}
								data-filtername={filterName}
								key={`${suggestion}-${i}`}
								className={classesSuggestion}
								onClick={() => onClick(disabled)}
								onFocus={() => setCurrentIndex(i)}
							>
								{prepend && <ExplanatoryText>{prepend} </ExplanatoryText>}
								{label}
								{append && <ExplanatoryText> {append}</ExplanatoryText>}
							</Suggestion>
						);
					})}
				</SuggestionsWrapper>
			</Wrapper>
		</>
	);
}

export const allowingTypes = Object.keys(searchTypes);

SearchBar.propTypes = {
	passedRef: PropTypes.object,
	searchesAllowing: PropTypes.arrayOf(PropTypes.oneOf(allowingTypes)),
	enableScroller: PropTypes.func.isRequired,
	disableScroller: PropTypes.func.isRequired,
	scrollerId: PropTypes.oneOf([DEFAULT, CRUISE_DETAIL_MODAL, FILTERS_MODAL]).isRequired,
	applyFiltersOnSelect: PropTypes.bool,
	placeholder: PropTypes.string,
	mockFilterOptions: PropTypes.arrayOf(PropTypes.shape({})),
};

export default withEnableDisableScroller(SearchBar);
